import { useCallback, useEffect, useState } from 'react';
import reviewService from '../../api/reviewService';
import { useNotifications } from '../../context/NotificationContext';
import Avatar from '../common/Avatar';
import StatusBadge from '../common/StatusBadge';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { currency } from '../../utils/formatters';

const TABS = ['PENDING', 'ACTIVE', 'REJECTED', ''];
const TAB_LABEL = { PENDING: 'Pending', ACTIVE: 'Approved', REJECTED: 'Rejected', '': 'All' };

export default function TechnicianApproval() {
  const notify = useNotifications();
  const [tab, setTab] = useState('PENDING');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    reviewService.technicianQueue({ page, size: 10, ...(tab ? { status: tab } : {}) })
      .then(setData)
      .catch((e) => notify.apiError(e))
      .finally(() => setLoading(false));
  }, [page, tab, notify]);

  useEffect(() => { load(); }, [load]);

  const approve = async (t) => {
    setBusy(t.id);
    try {
      await reviewService.approveTechnician(t.id);
      notify.success(`${t.firstName} ${t.lastName} approved`);
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  const reject = async (t) => {
    const reason = window.prompt(`Reject ${t.firstName} ${t.lastName}? Optional reason:`, '');
    if (reason === null) return;
    setBusy(t.id);
    try {
      await reviewService.rejectTechnician(t.id, reason);
      notify.success('Technician rejected');
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem' }}>
        <h1>Technician applications</h1>
        <div className="toolbar">
          {TABS.map((s) => (
            <button key={s || 'all'} className={`chip ${tab === s ? 'active' : ''}`}
              onClick={() => { setTab(s); setPage(0); }}>{TAB_LABEL[s]}</button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data.content.length === 0 ? (
          <EmptyState icon="person-check" title="Nothing to review" />
        ) : (
          <div className="stack">
            {data.content.map((t) => (
              <div key={t.id} className="card pad stack" style={{ gap: '.6rem' }}>
                <div className="spread">
                  <div className="row" style={{ alignItems: 'center' }}>
                    <Avatar src={t.profilePictureUrl} name={`${t.firstName} ${t.lastName}`} />
                    <div>
                      <strong>{t.firstName} {t.lastName}</strong>
                      <p className="muted" style={{ fontSize: '.85rem' }}>
                        {t.technicianTypeName} · {t.email}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={t.availableForWork ? 'ACTIVE' : 'PENDING'} />
                </div>
                <div className="row" style={{ gap: '1.5rem', fontSize: '.88rem' }}>
                  <span className="muted">{currency(t.hourlyRate)}/hr</span>
                  {t.yearsOfExperience != null && <span className="muted">{t.yearsOfExperience} yrs exp</span>}
                  {(t.city || t.state) && <span className="muted">{[t.city, t.state].filter(Boolean).join(', ')}</span>}
                </div>
                {t.bio && <p style={{ fontSize: '.9rem' }}>{t.bio}</p>}
                {t.certificationUrls?.length > 0 && (
                  <div className="tags">
                    {t.certificationUrls.map((u) => (
                      <a key={u} href={u} target="_blank" rel="noreferrer" className="badge blue">
                        <i className="bi bi-file-earmark" /> certificate
                      </a>
                    ))}
                  </div>
                )}
                {tab === 'PENDING' && (
                  <div className="row" style={{ gap: '.5rem' }}>
                    <button className="btn sm accent" disabled={busy === t.id} onClick={() => approve(t)}>Approve</button>
                    <button className="btn sm danger" disabled={busy === t.id} onClick={() => reject(t)}>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
